import React from "react";
import { SPThunkAction } from "common/ReduxUtils";
import { logError } from "Logger";
import { FormattedMessage } from "react-intl";
import { toast } from "react-toastify";
import { selectTransferCreate } from "transfers/create/store/TransferCreateSelectors";
import { createDraftReplenishmentOrder } from "./createDraftReplenishmentOrder";
import { updateDraftReplenishmentOrder } from "./updateDraftReplenishmentOrder";
import { transferCreateSetReplenishmentOrder } from "transfers/create/actions";
import { OrderResponse, ReplenishmentErrorCodes } from "@deliverr/replenishment-client";
import { getReplenishmentUnifiedInventory } from "../replenishment/warehouse-selection/actions/getReplenishmentUnifiedInventory";
import { isEmpty } from "lodash";

/**
 * Saves a draft replenishment order.
 * If an order ID exists, it updates the draft order.
 * Otherwise, it creates a new draft replenishment order.
 * @returns A thunk action that saves the draft order and returns a boolean indicating success or failure.
 */
export const saveDraftReplenishmentOrder =
  (): SPThunkAction<Promise<OrderResponse | undefined>> => async (dispatch, getState) => {
    const { replenishmentOrder, selectedProducts } = selectTransferCreate(getState());
    const orderId = replenishmentOrder?.orderId;
    let order: OrderResponse | undefined;
    try {
      if (orderId) {
        order = await dispatch(updateDraftReplenishmentOrder());
      } else {
        order = await dispatch(createDraftReplenishmentOrder());
      }
    } catch (error) {
      const errorData = error.response?.data?.error;

      const invalidOrderId = errorData?.payload?.orderId;
      if (invalidOrderId) {
        dispatch(transferCreateSetReplenishmentOrder({ orderId: invalidOrderId } as any));
      }

      if (errorData.code === ReplenishmentErrorCodes.INVALID_ORDER_ITEMS) {
        const dskus = Object.keys(selectedProducts);
        dispatch(getReplenishmentUnifiedInventory(dskus));
      }

      logError(
        {
          fn: "transfers.create.steps.commons.actions.saveDraftReplenishmentOrder",
          orderId,
        },
        error,
        "Error creating or updating draft replenishment order"
      );

      if (orderId) {
        toast.error(
          <FormattedMessage
            id="transfers.create.steps.commons.actions.saveDraftReplenishmentOrder.updateError"
            defaultMessage="Error while updating the order."
          />
        );
      } else {
        toast.error(
          <FormattedMessage
            id="transfers.create.steps.commons.actions.saveDraftReplenishmentOrder.createError"
            defaultMessage="Error while creating the order."
          />
        );
      }
    }

    return isEmpty(order) ? undefined : order;
  };
